
import React, { useState, useEffect } from 'react';
import { X, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function PWAInstallPrompt() {
  const [isVisible, setIsVisible] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    // Check if user has already dismissed the prompt
    const hasDismissed = localStorage.getItem('pwa-prompt-dismissed');
    
    // Only show on mobile devices and if not previously dismissed
    if (isMobile && !hasDismissed) {
      // Show after a short delay to not be too intrusive
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [isMobile]);

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
        setIsVisible(false);
      }
    } else {
      // Fallback for iOS Safari - show instructions
      alert('To install CSO Agent:\n\n1. Tap the Share button\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to install');
    }
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  if (!isVisible || !isMobile) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 bg-white border border-gray-200 rounded-lg shadow-lg p-4 mx-auto max-w-sm safe-area-bottom">
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">
          <img 
            src="/lovable-uploads/a69001e6-3781-413e-99e9-b39f99d15019.png" 
            alt="CSO Agent" 
            className="w-8 h-8 rounded"
          />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 mb-1">
            Install CSO Agent
          </p>
          <p className="text-xs text-gray-600 mb-3">
            Add to your home screen for quick access and a native app experience.
          </p>
          
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={handleInstall}
              className="flex items-center gap-1 text-xs min-h-[44px] px-4 touch-manipulation"
            >
              <Download className="w-3 h-3" />
              Install
            </Button>
            
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-xs min-h-[44px] px-4 touch-manipulation"
            >
              Not now
            </Button>
          </div>
        </div>
        
        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="flex-shrink-0 min-h-[44px] min-w-[44px] p-0 touch-manipulation"
          aria-label="Dismiss install prompt"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}
